/**
 * Projects API
 * Handles all project-related API calls
 */
import apiClient from './apiClient';

export type ProjectType = 'adm' | 'tnm' | 'staff_aug' | 'inhouse' | 'digital_marketing' | 'upwork' | 'outsource';

export interface ProjectMilestone {
  id: number;
  project_id: number;
  name: string;
  start_date: string | null;
  end_date: string | null;
  is_completed: boolean;
  is_overdue: boolean;
  completed_at: string | null;
  completed_by: number | null;
  completed_by_name: string | null;
  created_at: string;
  updated_at: string | null;
}

export interface ProjectClientContact {
  id: number;
  project_id: number;
  name: string;
  email: string | null;
  phone: string | null;
  created_at: string;
  resources?: ProjectResource[];
}

export interface ProjectResource {
  id: number;
  project_id: number;
  client_contact_id: number | null;
  employee_id: number | null;
  employee_name: string | null;
  employee_code: string | null;
  start_date: string | null;
  end_date: string | null;
  is_active: boolean;
  created_at: string;
}

export interface Project {
  id: number;
  client_id: number | null;
  client_name: string | null;
  name: string;
  code: string | null;
  description: string | null;
  manager_id: number | null;
  manager_name: string | null;
  sales_person_id: number | null;
  sales_person_name: string | null;
  start_date: string | null;
  end_date: string | null;
  status: 'active' | 'completed' | 'on_hold' | 'cancelled';
  is_billable: boolean;
  project_type: ProjectType | null;
  monthly_hours: number | null;
  total_months: number | null;
  created_at: string;
  updated_at: string | null;
  // Type-specific details (only present when fetched with include_type_details)
  milestones?: ProjectMilestone[];
  client_contacts?: ProjectClientContact[];
  resources?: ProjectResource[];
}

export interface ProjectsListResponse {
  projects: Project[];
  total: number;
  page: number;
  per_page: number;
  pages: number;
}

export interface ProjectsQueryParams {
  status?: string;
  client_id?: number;
  manager_id?: number;
  project_type?: string;
  is_billable?: string;
  search?: string;
  page?: number;
  per_page?: number;
  // Column-specific searches
  search_project?: string;
  search_code?: string;
  search_client?: string;
  search_manager?: string;
  [key: string]: string | number | undefined;
}

export interface ProjectGroupNode {
  label: string;
  field: string;
  field_value: string;
  key: string;
  count: number;
  sub_groups?: ProjectGroupNode[];
}

export interface GroupedProjectsParams {
  group_by: string;
  status?: string;
  client_id?: number;
  manager_id?: number;
  project_type?: string;
  is_billable?: string;
  search?: string;
  // Column-specific searches
  search_project?: string;
  search_code?: string;
  search_client?: string;
  search_manager?: string;
  [key: string]: string | number | undefined;
}

export interface AssignmentHistoryItem {
  id: number;
  project_id: number;
  project_name: string | null;
  employee_id: number;
  employee_name: string | null;
  employee_email: string | null;
  employee_code: string | null;
  assigned_by: number | null;
  assigned_by_name: string | null;
  assigned_at: string | null;
  start_date: string | null;
  end_date: string | null;
  removed_at: string | null;
  is_active: boolean;
}

export interface AssignmentHistoryResponse {
  assignments: AssignmentHistoryItem[];
  total: number;
  page: number;
  per_page: number;
  pages: number;
}

export interface AssignmentHistoryParams {
  page?: number;
  per_page?: number;
  status?: 'all' | 'active' | 'removed';
  employee_id?: number;
  project_id?: number;
  start_date?: string;
  end_date?: string;
  search?: string;
}

export const projectsApi = {
  /**
   * Get all projects (filtered by role)
   */
  getProjects: async (params?: ProjectsQueryParams): Promise<ProjectsListResponse> => {
    const queryParams = new URLSearchParams();
    if (params) {
      Object.entries(params).forEach(([key, value]) => {
        if (value !== undefined && value !== null && value !== '') {
          queryParams.append(key, String(value));
        }
      });
    }

    const query = queryParams.toString();
    return apiClient.get<ProjectsListResponse>(`/projects${query ? `?${query}` : ''}`);
  },

  /**
   * Get projects grouped by a field
   */
  getProjectsGrouped: async (params: GroupedProjectsParams): Promise<{ groups: ProjectGroupNode[]; total: number; group_by: string[] }> => {
    const queryParams = new URLSearchParams();
    Object.entries(params).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== '') {
        queryParams.append(key, String(value));
      }
    });
    return apiClient.get(`/projects/grouped?${queryParams.toString()}`);
  },

  /**
   * Export projects to CSV or XLSX
   */
  exportProjects: async (params?: {
    format?: 'csv' | 'xlsx';
    status?: string;
    client_id?: number;
    manager_id?: number;
    search?: string;
    columns?: string;
  }): Promise<Blob> => {
    const queryParams = new URLSearchParams();
    if (params?.format) queryParams.append('format', params.format);
    if (params?.status) queryParams.append('status', params.status);
    if (params?.client_id) queryParams.append('client_id', String(params.client_id));
    if (params?.manager_id) queryParams.append('manager_id', String(params.manager_id));
    if (params?.search) queryParams.append('search', params.search);
    if (params?.columns) queryParams.append('columns', params.columns);
    const query = queryParams.toString();
    return apiClient.getBlob(`/projects/export${query ? `?${query}` : ''}`);
  },

  /**
   * Get projects assigned to current employee
   */
  getMyProjects: async (): Promise<{ projects: Project[]; total: number }> => {
    return apiClient.get<{ projects: Project[]; total: number }>('/projects/my-projects');
  },

  /**
   * Get projects NOT assigned to current employee (for request dropdown)
   */
  getUnassignedProjects: async (): Promise<{ projects: Project[]; total: number }> => {
    return apiClient.get<{ projects: Project[]; total: number }>('/projects/unassigned');
  },

  /**
   * Get a specific project by ID
   */
  getProject: async (projectId: number): Promise<{ project: Project }> => {
    return apiClient.get<{ project: Project }>(`/projects/${projectId}`);
  },

  /**
   * Get project team members
   */
  getProjectTeam: async (projectId: number): Promise<{
    project_id: number;
    project_name: string;
    project_type: string | null;
    team: Array<{
      assignment_id: number;
      employee_id: number;
      employee_name: string;
      employee_email: string;
      assigned_at: string;
      start_date: string | null;
      end_date: string | null;
    }>;
    total: number;
  }> => {
    return apiClient.get(`/projects/${projectId}/team`);
  },

  /**
   * Create a new project (PMO/SuperAdmin only)
   */
  createProject: async (data: {
    name: string;
    client_id?: number;
    code?: string;
    description?: string;
    manager_id?: number;
    sales_person_id?: number;
    start_date?: string;
    end_date?: string;
    status?: string;
    is_billable?: boolean;
    project_type?: ProjectType;
    monthly_hours?: number;
    total_months?: number;
    milestones?: Array<{ name: string; start_date?: string; end_date?: string }>;
    client_contacts?: Array<{ name: string; email?: string; phone?: string }>;
    resources?: Array<{ employee_id?: number; start_date?: string; end_date?: string }>;
  }): Promise<{ message: string; project: Project }> => {
    return apiClient.post('/projects', data);
  },

  /**
   * Update a project (PMO/SuperAdmin only)
   */
  updateProject: async (projectId: number, data: Partial<{
    name: string;
    client_id: number;
    code: string;
    description: string;
    manager_id: number;
    sales_person_id: number;
    start_date: string;
    end_date: string;
    status: string;
    is_billable: boolean;
    project_type: ProjectType;
    monthly_hours: number;
    total_months: number;
  }>): Promise<{ message: string; project: Project }> => {
    return apiClient.put(`/projects/${projectId}`, data);
  },

  /**
   * Assign employee to project
   */
  assignEmployee: async (projectId: number, employeeId: number, startDate?: string, endDate?: string): Promise<{
    message: string;
    assignment: {
      id: number;
      project_id: number;
      employee_id: number;
      assigned_at: string;
      start_date: string | null;
      end_date: string | null;
    };
  }> => {
    const body: any = { employee_id: employeeId };
    if (startDate) body.start_date = startDate;
    if (endDate) body.end_date = endDate;
    return apiClient.post(`/projects/${projectId}/assign`, body);
  },

  /**
   * Deassign (soft-remove) employee from project, setting end_date
   */
  deassignEmployee: async (projectId: number, employeeId: number, endDate?: string): Promise<{ message: string }> => {
    const body: any = {};
    if (endDate) body.end_date = endDate;
    return apiClient.put(`/projects/${projectId}/deassign/${employeeId}`, body);
  },

  /**
   * Remove employee from project (hard remove via DELETE — kept for backward compat)
   */
  removeEmployee: async (projectId: number, employeeId: number): Promise<{ message: string }> => {
    return apiClient.delete(`/projects/${projectId}/assign/${employeeId}`);
  },

  /**
   * Bulk assign employees to project
   */
  bulkAssignEmployees: async (projectId: number, employeeIds: number[], startDate?: string, endDate?: string): Promise<{
    message: string;
    successful: number;
    failed: number;
  }> => {
    // Call individual assign for each employee (backend doesn't have bulk endpoint yet)
    let successful = 0;
    let failed = 0;
    
    for (const employeeId of employeeIds) {
      try {
        const body: any = { employee_id: employeeId };
        if (startDate) body.start_date = startDate;
        if (endDate) body.end_date = endDate;
        await apiClient.post(`/projects/${projectId}/assign`, body);
        successful++;
      } catch {
        failed++;
      }
    }
    
    return {
      message: `Assigned ${successful} employees, ${failed} failed`,
      successful,
      failed
    };
  },

  /**
   * Delete a project (SuperAdmin only)
   */
  deleteProject: async (projectId: number): Promise<{ message: string }> => {
    return apiClient.delete(`/projects/${projectId}`);
  },

  // ==========================================================================
  // MILESTONE ENDPOINTS (ADM projects)
  // ==========================================================================

  getMilestones: async (projectId: number): Promise<{ milestones: ProjectMilestone[]; total: number }> => {
    return apiClient.get(`/projects/${projectId}/milestones`);
  },

  createMilestone: async (projectId: number, data: {
    name: string;
    start_date?: string;
    end_date?: string;
  }): Promise<{ message: string; milestone: ProjectMilestone }> => {
    return apiClient.post(`/projects/${projectId}/milestones`, data);
  },

  updateMilestone: async (projectId: number, milestoneId: number, data: Partial<{
    name: string;
    start_date: string;
    end_date: string;
  }>): Promise<{ message: string; milestone: ProjectMilestone }> => {
    return apiClient.put(`/projects/${projectId}/milestones/${milestoneId}`, data);
  },

  completeMilestone: async (projectId: number, milestoneId: number): Promise<{ message: string; milestone: ProjectMilestone }> => {
    return apiClient.put(`/projects/${projectId}/milestones/${milestoneId}/complete`, {});
  },

  deleteMilestone: async (projectId: number, milestoneId: number): Promise<{ message: string }> => {
    return apiClient.delete(`/projects/${projectId}/milestones/${milestoneId}`);
  },

  // ==========================================================================
  // CLIENT CONTACT ENDPOINTS (Staff Aug projects)
  // ==========================================================================

  getClientContacts: async (projectId: number): Promise<{ client_contacts: ProjectClientContact[]; total: number }> => {
    return apiClient.get(`/projects/${projectId}/client-contacts`);
  },

  createClientContact: async (projectId: number, data: {
    name: string;
    email?: string;
    phone?: string;
  }): Promise<{ message: string; client_contact: ProjectClientContact }> => {
    return apiClient.post(`/projects/${projectId}/client-contacts`, data);
  },

  updateClientContact: async (projectId: number, contactId: number, data: Partial<{
    name: string;
    email: string;
    phone: string;
  }>): Promise<{ message: string; client_contact: ProjectClientContact }> => {
    return apiClient.put(`/projects/${projectId}/client-contacts/${contactId}`, data);
  },

  deleteClientContact: async (projectId: number, contactId: number): Promise<{ message: string }> => {
    return apiClient.delete(`/projects/${projectId}/client-contacts/${contactId}`);
  },

  // ==========================================================================
  // RESOURCE ENDPOINTS (Staff Aug projects)
  // ==========================================================================

  getResources: async (projectId: number): Promise<{ resources: ProjectResource[]; total: number }> => {
    return apiClient.get(`/projects/${projectId}/resources`);
  },

  createResource: async (projectId: number, data: {
    client_contact_id?: number;
    employee_id?: number;
    start_date?: string;
    end_date?: string;
  }): Promise<{ message: string; resource: ProjectResource }> => {
    return apiClient.post(`/projects/${projectId}/resources`, data);
  },

  updateResource: async (projectId: number, resourceId: number, data: Partial<{
    employee_id: number;
    start_date: string;
    end_date: string;
    is_active: boolean;
  }>): Promise<{ message: string; resource: ProjectResource }> => {
    return apiClient.put(`/projects/${projectId}/resources/${resourceId}`, data);
  },

  deleteResource: async (projectId: number, resourceId: number): Promise<{ message: string }> => {
    return apiClient.delete(`/projects/${projectId}/resources/${resourceId}`);
  },

  /**
   * Get the history of project assignments (active + removed/deassigned).
   */
  getAssignmentHistory: async (params?: AssignmentHistoryParams): Promise<AssignmentHistoryResponse> => {
    const queryParams = new URLSearchParams();
    if (params) {
      Object.entries(params).forEach(([key, value]) => {
        if (value !== undefined && value !== null && value !== '') {
          queryParams.append(key, String(value));
        }
      });
    }
    const query = queryParams.toString();
    return apiClient.get<AssignmentHistoryResponse>(
      `/projects/assignments/history${query ? `?${query}` : ''}`
    );
  },
};

export default projectsApi;
